/* Offertes van het project: wat in het CRM is opgesteld (quotes) én offertes die
 * alleen als bestand in de projectmap staan, zoals een getekende PDF. Die tweede
 * soort had op de oude pagina geen plek. Een PDF die als revisie van een CRM-offerte
 * in de map staat (ADR 0018) hoort bij die offerte en staat er niet nog eens los. */

import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { DocumentGroup } from '../../../../supabase/functions/_shared/fileVersions';
import type { Bestand } from '../data/bestanden';
import type { PaginaProject, Offerte } from '../data/project';
import { useMaakOfferte } from '../hooks';
import { lokaleDag, weergave, WEERGAVE_KLEUR, WEERGAVE_LABEL } from '../../offertes/data/status';
import { datum, euro } from './opmaak';

/**
 * Documenten in de projectmap die een offerte zijn: offertenummer in de naam, of het
 * woord. Zonder de PDF's die al als revisie bij een CRM-offerte horen.
 */
export function offerteDocumenten(docs: DocumentGroup<Bestand>[], gekoppeld: Set<string> = new Set()): DocumentGroup<Bestand>[] {
  return docs.filter(
    (d) => (d.scheme === 'offerte' || /offerte|quote/i.test(d.title)) && !d.versions.some((v) => v.files.some((f) => gekoppeld.has(f.file.name))),
  );
}

export const gekoppeldeBestanden = (offertes: Offerte[]) => new Set(offertes.flatMap((q) => q.bestanden ?? []));

function Status({ label, klasse }: { label: string; klasse: string }) {
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${klasse}`}>{label}</span>;
}

const RIJ = 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 px-5 py-3.5 text-sm md:grid-cols-[minmax(0,1fr)_130px_110px_110px_36px]';

export function OffertesBlok({ project, offertes, documenten }: {
  project: PaginaProject;
  offertes: Offerte[];
  documenten: DocumentGroup<Bestand>[];
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const maak = useMaakOfferte();
  const pdfs = offerteDocumenten(documenten, gekoppeldeBestanden(offertes));
  const vandaag = lokaleDag();
  const aantal = offertes.length + pdfs.length;

  const nieuw = () =>
    maak.mutate(project, {
      onSuccess: (id) => navigate(`/crm/quotes/${id}`),
      onError: (e) => toast({ title: 'Offerte aanmaken mislukt', description: (e as Error).message, variant: 'destructive' }),
    });

  return (
    <section id="offertes" aria-labelledby="pagina-offertes" className="flex scroll-mt-14 flex-col gap-3.5">
      <div className="flex items-center justify-between gap-3">
        <h2 id="pagina-offertes" className="text-lg font-semibold">
          Offertes <span className="font-normal text-muted-foreground">{aantal}</span>
        </h2>
        <Button variant="outline" size="sm" onClick={nieuw} disabled={maak.isPending || !project.org_id}
          title={project.org_id ? undefined : 'Koppel eerst een organisatie aan dit project'}>
          <Plus className="mr-1.5 h-4 w-4" />
          Nieuwe offerte
        </Button>
      </div>
      <div className="rounded-xl border bg-background">
        {aantal === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">Nog geen offerte bij dit project.</p>
        ) : (
          <>
            <div className="hidden gap-4 border-b px-5 py-2.5 text-xs text-muted-foreground md:grid md:grid-cols-[minmax(0,1fr)_130px_110px_110px_36px]">
              <span>Offerte</span><span>Status</span><span>Datum</span><span>Bedrag</span><span />
            </div>
            <ul className="divide-y">
              {offertes.map((q) => {
                const w = weergave(q, vandaag);
                const s = { label: WEERGAVE_LABEL[w], klasse: WEERGAVE_KLEUR[w] };
                return (
                  <li key={q.id}>
                    <Link to={`/crm/quotes/${q.id}`} className={`${RIJ} hover:bg-muted/50`}>
                      <span className="flex min-w-0 items-center gap-3">
                        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                          <FileText className="h-4 w-4" aria-hidden />
                        </span>
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-semibold">{[q.quote_number, q.title].filter(Boolean).join(' · ')}</span>
                          <span className="text-xs text-muted-foreground">
                            {q.current_version && q.current_version > 1 ? `Revisie ${q.current_version} · ` : ''}
                            {q.bestanden?.length ? 'PDF in de projectmap' : 'opgesteld in het CRM'}
                          </span>
                        </span>
                      </span>
                      <span><Status {...s} /></span>
                      <span className="hidden text-muted-foreground md:block">{datum(q.quote_date ?? q.updated_at)}</span>
                      <span className="hidden font-medium tabular-nums md:block">{euro(q.total_amount)}</span>
                      <span className="hidden md:block" />
                    </Link>
                  </li>
                );
              })}
              {pdfs.map((d) => {
                const nieuwste = d.versions[0]!.files[0]!;
                const getekend = /getekend|ondertekend|signed/i.test(nieuwste.file.name);
                return (
                  <li key={d.key}>
                    <a href={nieuwste.file.web_url ?? '#'} target="_blank" rel="noreferrer" className={`${RIJ} hover:bg-muted/50`}>
                      <span className="flex min-w-0 items-center gap-3">
                        <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-red-50 text-[10px] font-bold text-red-700">PDF</span>
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-semibold">{d.title}</span>
                          <span className="text-xs text-muted-foreground">
                            {d.versions[0]!.version !== '1' || d.versions.length > 1 ? `Versie ${d.versions[0]!.version} · ` : ''}uit de projectmap
                          </span>
                        </span>
                      </span>
                      <span><Status label={getekend ? 'Getekend' : 'Bestand'} klasse={getekend ? 'bg-green-50 text-green-800 border-green-200' : 'bg-muted text-foreground'} /></span>
                      <span className="hidden text-muted-foreground md:block">{datum(nieuwste.file.modified)}</span>
                      <span className="hidden text-muted-foreground md:block">—</span>
                      <ExternalLink className="hidden h-4 w-4 text-muted-foreground md:block" aria-label="Openen in SharePoint" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
